import Dexie, { Table } from 'dexie';
import { DistrictDemoData, State } from './pricing-tool-api';

const TTL_HOURS = 12;
const TTL_SECONDS = 60 * 60 * TTL_HOURS;

// Bump this whenever the demo-data response shape changes, OR whenever the
// underlying voterfile data is refreshed — cached entries stamped with an older
// version (or no version at all) are evicted on next read regardless of TTL.
// Without a bump, a warm cache keeps serving the previous voterfile for up to
// TTL_HOURS after a deploy, and the only fix is asking people to clear their
// browser storage.
//
// Track the voterfile vintage this cache holds, so "did I bump it?" is
// checkable against the export manifest at
// gs://data-processing-jobs/pricing_district_demos/000000000000.manifest.json
// rather than remembered.
//   v2026.05.1: added dem_primary_max_sample_size
//   v2026.09.1: voterfile vintage 20260909 (144,509 districts)
const CURRENT_SCHEMA_VERSION = 'v2026.09.1';

export interface StateDemographicData {
id?: number;
expiry: Date;
state: string;
data: Array<DistrictDemoData>;
schemaVersion?: string;
}

export class MyDexie extends Dexie {
  demographicData!: Table<StateDemographicData>;

  constructor() {
    super('pricing-tool');
    this.version(2).stores({
      // represents the indexes only, you don't have to define all the data here. See interface for all fields
      demographicData: '++id,expiry,state',
    });
  }
}

export const db = new MyDexie();

export const saveDemographicDataToIndexed = async (
  state: State,
  data: Array<DistrictDemoData>,
  ttlSeconds = TTL_SECONDS,
) => {
  const expiryTime = new Date(Date.now() + ttlSeconds * 1000);
  const id = await db.demographicData.add({
    expiry: expiryTime,
    state,
    data,
    schemaVersion: CURRENT_SCHEMA_VERSION,
  });
  return id;
};

export const getDemographicDataFromIndexed = async (
  state: State,
  allowStale = false,
  deleteOnStale = true,
) => {
  const value = await db.demographicData.where('state').equals(state).first();
  if (value) {
    const { expiry, data, schemaVersion } = value;
    const isExpired =
      expiry < new Date(Date.now()) ||
      schemaVersion !== CURRENT_SCHEMA_VERSION;
    if (isExpired && deleteOnStale && value.id) {
      console.log(`State IndexDB: expiring ${state}`);
      await db.demographicData.delete(value.id);
    }
    if (allowStale || !isExpired) {
      return data;
    }
  }
  return null;
};
